import CustomTable from '../../components/table'
import { Button, Divider, Form, Input, Modal, Space, Switch, TableProps, Tag, Typography } from 'antd'
import { EditOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons'
import defaultParams from '../../assets/constants/default_params'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useLoading } from '../../context/LoadingContext'
import { apiAddCompanyNotifyConfig, apiGetCompanyNotifyConfig, apiUpdateCompanyNotifyConfig } from '../../api'
import { toastError, toastSuccess } from '../../utils/toast'

const NOTIFY_METHOD_TELEGRAM = 'telegram'
const NOTIFY_METHOD_SLACK = 'slack'
const NOTIFY_METHOD_ZNS = 'zns'

type FieldDef = {
    name: string
    label: string
    required?: boolean
    placeholder?: string
}

type MethodDef = {
    name: string
    color: string
    description: string
    configKey: string
    fields: FieldDef[]
}

const METHODS: Record<string, MethodDef> = {
    [NOTIFY_METHOD_ZNS]: {
        name: 'ZNS',
        color: 'cyan',
        description: 'Dịch vụ tin nhắn Zalo Official Account (ZNS)',
        configKey: 'zns',
        fields: [
            { name: 'templateId', label: 'Template ID', required: true, placeholder: 'Nhập Template ID' },
        ],
    },
    [NOTIFY_METHOD_TELEGRAM]: {
        name: 'Telegram',
        color: 'blue',
        description: 'Bot gửi thông báo vào chat hoặc topic Telegram',
        configKey: 'telegram',
        fields: [
            { name: 'chatId', label: 'Chat ID', required: true, placeholder: 'Nhập Chat ID' },
            { name: 'botToken', label: 'Bot token', required: true, placeholder: 'Nhập Bot token' },
            { name: 'messageThreadId', label: 'Message thread ID', placeholder: 'Nhập thread ID nếu dùng topic' },
        ],
    },
    [NOTIFY_METHOD_SLACK]: {
        name: 'Slack',
        color: 'purple',
        description: 'Webhook gửi thông báo vào channel Slack',
        configKey: 'slack',
        fields: [
            { name: 'channel', label: 'Channel', placeholder: 'Nhập Channel' },
            { name: 'username', label: 'Username', placeholder: 'Nhập Username hiển thị' },
            { name: 'endpoint', label: 'Endpoint', required: true, placeholder: 'Nhập webhook endpoint' },
        ],
    },
}

const methodOptions = Object.entries(METHODS).map(([key, cfg]) => ({
    key,
    name: cfg.name,
    color: cfg.color,
    description: cfg.description,
}))

const getMethodColor = (method?: string) => (method ? METHODS[method]?.color : undefined)

const getDefaultConfig = (id: number, cpCode: string) => ({
    id,
    cpCode,
    notifyConfig: {
        method: '',
        ...Object.fromEntries(
            Object.values(METHODS).map(cfg => [
                cfg.configKey,
                Object.fromEntries(cfg.fields.map(f => [f.name, ''])),
            ]),
        ),
    },
})

export default function TabNotifyConfig({ id, cpCode, templateId }: any) {
    const [config, setConfig] = useState<any>(undefined)
    const [currentPage, setCurrentPage] = useState<number>(defaultParams.PAGE)
    const [pageSize, setPageSize] = useState<number>(defaultParams.LIMIT)
    const [modalConfig, setModalConfig] = useState<{ method: string, isOpen: boolean }>({
        method: '',
        isOpen: false,
    })
    const [formConfig] = Form.useForm()
    const formValues = Form.useWatch([], formConfig)
    const { setIsLoading } = useLoading()
    const activeMethodConfig = METHODS[modalConfig.method]

    const resolveFieldValue = (method: string, fieldName: string, value: any) => {
        if (method === NOTIFY_METHOD_ZNS && fieldName === 'templateId') {
            return value || templateId || ''
        }
        return value || ''
    }

    const fetchCompanyNotifyConfig = useCallback(async () => {
        try {
            if (!cpCode) return
            setIsLoading(true)
            const { data } = await apiGetCompanyNotifyConfig({ cpCode: cpCode, id: id })
            const item = data?.config?.id ? data.config : null
            setConfig(item)
        } catch (error) {
            console.error(error)
            setConfig(null)
        } finally {
            setIsLoading(false)
        }
    }, [cpCode, setIsLoading, id])

    useEffect(() => {
        fetchCompanyNotifyConfig()
    }, [fetchCompanyNotifyConfig])

    const activeMethod = useMemo(() => {
        if (config === undefined) return ''
        if (config === null) {
            return templateId ? NOTIFY_METHOD_ZNS : ''
        }
        return config.notifyConfig?.method || ''
    }, [config, templateId])

    const rows = useMemo(() => methodOptions.map(method => {
        const notifyConfig = config?.notifyConfig ? config.notifyConfig : getDefaultConfig(id, cpCode).notifyConfig || {}
        const configKey = METHODS[method.key]?.configKey
        return {
            ...method,
            isActive: activeMethod === method.key,
            config: notifyConfig[configKey] || {},
        }
    }), [config, cpCode, id, activeMethod])

    const isSwitchDisabled = useMemo(() => {
        const requiredFields = activeMethodConfig?.fields.filter(f => f.required) || []
        if (requiredFields.length === 0) return true
        return requiredFields.some(f => !resolveFieldValue(modalConfig.method, f.name, formValues?.[f.name]))
    }, [formValues, modalConfig.method, templateId])

    const openConfigModal = (method: string) => {
        const notifyConfig = config?.notifyConfig || getDefaultConfig(id, cpCode).notifyConfig
        const cfg = METHODS[method]
        const methodConfig = notifyConfig?.[cfg?.configKey || ''] || {}

        setModalConfig({ method, isOpen: true })
        formConfig.setFieldsValue({
            active: activeMethod === method,
            ...Object.fromEntries(
                (cfg?.fields || []).map(f => [f.name, resolveFieldValue(method, f.name, methodConfig[f.name])]),
            ),
        })
    }

    const closeConfigModal = () => {
        setModalConfig({ method: '', isOpen: false })
        formConfig.resetFields()
    }

    const buildPayload = (values: any) => {
        const current = config || getDefaultConfig(id, cpCode)
        const notifyConfig = config?.notifyConfig || getDefaultConfig(id, cpCode).notifyConfig
        const currentMethod = config === null && templateId ? NOTIFY_METHOD_ZNS : notifyConfig.method
        const cfg = METHODS[modalConfig.method]

        const updatedMethodConfig = Object.fromEntries(
            (cfg?.fields || []).map(f => [f.name, resolveFieldValue(modalConfig.method, f.name, values[f.name])]),
        )

        const nextNotifyConfig = {
            method: values.active ? modalConfig.method : currentMethod === modalConfig.method ? '' : currentMethod,
            ...Object.fromEntries(Object.values(METHODS).map(m => [m.configKey, notifyConfig[m.configKey] || {}])),
            ...(cfg && { [cfg.configKey]: updatedMethodConfig }),
        }

        return {
            ...current,
            id: id || current.id || 0,
            cpCode: cpCode || current.cpCode || '',
            notifyConfig: nextNotifyConfig,
        }
    }

    const saveConfig = async (
        payload: any,
        closeModal = false,
    ) => {
        try {
            if (!id || !cpCode) {
                toastError('Thiếu thông tin công ty để lưu cấu hình!')
                return
            }
            setIsLoading(true)
            if (config) {
                await apiUpdateCompanyNotifyConfig({ config: payload })
                toastSuccess('Cập nhật cấu hình thông báo thành công!')
            } else {
                await apiAddCompanyNotifyConfig({ config: payload })
                toastSuccess('Thêm cấu hình thông báo thành công!')
            }
            if (closeModal) {
                closeConfigModal()
            }
            fetchCompanyNotifyConfig()
        } catch (error) {
            console.error(error)
            toastError('Lưu cấu hình thông báo thất bại!')
        } finally {
            setIsLoading(false)
        }
    }

    const handleSaveConfig = async (values: any) => {
        const payload = buildPayload(values)
        await saveConfig(
            payload,
            true,
        )
    }

    const renderConfigSummary = (method: string, data: any) => {
        const cfg = METHODS[method]
        if (!cfg) return null
        return (
            <Space direction="vertical" size={0}>
                {cfg.fields.map(field => {
                    const value = resolveFieldValue(method, field.name, data?.[field.name])
                    return (
                        <span key={field.name}>
                            {field.label}: {value || <span style={{ color: '#999' }}>Chưa cấu hình</span>}
                        </span>
                    )
                })}
            </Space>
        )
    }

    const columns: TableProps<any>['columns'] = [
        {
            key: 'method',
            title: 'Phương thức',
            render: (_, data) => (
                <Space direction="vertical" size={0}>
                    <Tag color={data.color} style={{ width: 90, textAlign: 'center', marginRight: 0 }}>{data.name}</Tag>
                    <span style={{ color: '#666' }}>{data.description}</span>
                </Space>
            ),
        },
        {
            key: 'status',
            title: 'Trạng thái',
            align: 'center',
            render: (_, data) => data.isActive
                ? <Tag color="green" style={{ marginRight: 0 }}>Đang hoạt động</Tag>
                : <Tag style={{ marginRight: 0 }}>Dự phòng</Tag>,
        },
        {
            key: 'config',
            title: 'Thông tin cấu hình',
            render: (_, data) => renderConfigSummary(data.key, data.config),
        },
        {
            key: 'action',
            title: 'Tác vụ',
            align: 'center',
            render: (_, data) => {
                return (
                    <Button
                        icon={config ? <EditOutlined /> : <PlusOutlined />}
                        onClick={() => openConfigModal(data.key)}
                        style={{ width: 110, background: '#3e64b5', color: 'white' }}
                    >
                        Cấu hình
                    </Button>
                )
            },
        },
    ]

    return (
        <div>
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 20,
                flexWrap: 'wrap',
                marginBottom: 20,
            }}>
                <Space direction="vertical" size={2}>
                    <Typography.Text strong>Phương thức đang hoạt động</Typography.Text>
                    <Tag color={getMethodColor(activeMethod)}>
                        {activeMethod || 'Không'}
                    </Tag>
                </Space>
                <Button icon={<ReloadOutlined />} onClick={fetchCompanyNotifyConfig}>Tải lại</Button>
            </div>
            <CustomTable
                namePage='cấu hình thông báo'
                columns={columns}
                dataSource={rows}
                pageSize={pageSize}
                setPageSize={setPageSize}
                total={rows.length}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
            />
            <Modal
                title={`Cấu hình ${modalConfig.method}`}
                open={modalConfig.isOpen}
                footer={null}
                onCancel={closeConfigModal}
                destroyOnClose>
                <Divider />
                <Form form={formConfig} onFinish={handleSaveConfig} layout="vertical" autoComplete="off">
                    <Form.Item name="active" label="Đặt làm phương thức hoạt động (vui lòng nhập đủ thông tin cần thiết)" valuePropName="checked">
                        <Switch disabled={isSwitchDisabled} />
                    </Form.Item>
                    {activeMethodConfig?.fields.map(field => (
                        <Form.Item
                            key={field.name}
                            name={field.name}
                            label={field.label}
                            rules={field.required ? [{ required: true, message: `Vui lòng nhập ${field.label}` }] : undefined}
                        >
                            <Input placeholder={field.placeholder} />
                        </Form.Item>
                    ))}
                    <Space style={{ display: 'flex', justifyContent: 'end' }}>
                        <Button onClick={closeConfigModal}>Huỷ</Button>
                        <Button type="primary" htmlType="submit">Lưu</Button>
                    </Space>
                </Form>
            </Modal>
        </div>
    )
}